import React, { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  MoreHorizontal,
  FileText,
  CheckCircle2,
  Clock,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ExcelColumnDef } from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { formatDate, cn } from "@/lib/utils";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  type FilterField,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";

import PrintDialog from "@/components/shared/PrintDialog";

const FILTER_FIELDS: FilterField[] = [
  CHECKBOX_COL_NULL,
  { key: "specId", label: "Lọc mã TSKT...", type: "text" },
  { key: "productName", label: "Lọc sản phẩm / KH...", type: "text" },
  { key: "dimensions", label: "Lọc kích thước...", type: "text" },
  null,
];

const STATUS_ITEMS: StatusSidebarItem[] = [
  {
    key: "APPROVED",
    label: "Hoàn thành",
    color: "bg-emerald-100 text-emerald-700",
    dotColor: "bg-emerald-500",
  },
  {
    key: "DRAFT",
    label: "Bản nháp",
    color: "bg-slate-100 text-slate-600",
    dotColor: "bg-slate-400",
  },
  {
    key: "PENDING",
    label: "Chờ cập nhật",
    color: "bg-amber-100 text-amber-700",
    dotColor: "bg-amber-500",
  },
];

/**
 * Cột Excel Tiêu chuẩn SP (05/11/2026): đúng trường của TSKT (mã, sản phẩm, khổ thành phẩm / trải, in, phiên bản),
 * xuất toàn bộ theo bộ lọc (trước chỉ trang đang xem và đọc sai trường nên cột kích thước / khách hàng trống).
 */
const SPEC_COLUMNS: ExcelColumnDef[] = [
  { key: "specId", label: "Mã TSKT" },
  { key: "productName", label: "Tên sản phẩm", format: (v, r: any) => v || r?.product?.productName || r?.product?.name || "" },
  { key: "product.productCode", label: "Mã sản phẩm", format: (v, r: any) => v || r?.product?.code || "" },
  { key: "product.customer.name", label: "Khách hàng", format: (v, r: any) => v || r?.product?.customer?.shortName || "" },
  { key: "finishedSize", label: "Khổ thành phẩm" },
  { key: "flatSize", label: "Khổ trải" },
  { key: "printTech", label: "Công nghệ in" },
  { key: "printColors", label: "Màu in" },
  { key: "version", label: "Phiên bản" },
  { key: "status", label: "Trạng thái", format: (v) => STATUS_ITEMS.find((s) => s.key === v)?.label || v || "" },
  { key: "createdAt", label: "Ngày tạo", format: "date" },
]

export default function TechnicalSpecsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [printSpec, setPrintSpec] = useState<any>(null);

  const {
    items,
    isLoading,
    totalItems,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    statusFilter,
    onStatusChange,
    filterValues,
    onFilterChange,
    showFilter,
    onToggleFilter,
    onClearAll,
    statusCounts: serverStatusCounts,
    serverParams,
  } = useServerListFilter<any>({
    queryKey: "technical-specs",
    queryFn: (params) => trpcClient.technical.specs.list.query(params) as any,
    defaultLimit: 50,
  });

  const rowIds = items.map((t: any) => t.tsktId || t.specId || t.id);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    handleRowSelect,
    toggleSelectAll,
    clearSelection,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
    selectedCount,
  } = useRowSelection(rowIds);

  const displayItems = useMemo(() => {
    return items.filter((item: any) => {
      const specId = (item.tsktId || item.specId || item.id || "").toLowerCase();
      if (filterValues.specId && !specId.includes(filterValues.specId.toLowerCase())) return false;
      const prodName = (item.product?.name || item.productName || item.customer?.name || item.customerName || "").toLowerCase();
      if (filterValues.productName && !prodName.includes(filterValues.productName.toLowerCase())) return false;
      const dim = `${item.length || item.dai || ""} x ${item.width || item.rong || ""} x ${item.height || item.cao || ""}`.toLowerCase();
      if (filterValues.dimensions && !dim.includes(filterValues.dimensions.toLowerCase())) return false;
      return true;
    });
  }, [items, filterValues]);

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    return [
      /* { label: "Xuất", icon: <Download className="w-3.5 h-3.5" />, onClick: () => {
                                const sel = (allOrders || allItems || allData || filtered || []).filter((o: any) => selectedIds.has(o.id || o.orderId || o.machineId || o.materialId || o.productId || o.receiptId || o.customerId || o.vendorId || o.deliveryId || o.checkId || o.userId));
                                if (!sel.length) return;
                                exportToExcel(sel as any, CSV_COLUMNS, 'danh_sach_xuat');
                                
                                clearSelection();
                            } */
    ];
  };

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => {
      const sc = serverStatusCounts?.find(
        (s: any) =>
          s.status?.toString()?.toUpperCase() === item.key?.toUpperCase(),
      );
      return {
        ...item,
        count: sc ? sc.count : 0,
      };
    });
  }, [serverStatusCounts]);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_tskt",
    sheetName: "TSKT",
    columns: SPEC_COLUMNS,
    queryFn: (params) => trpcClient.technical.specs.list.query(params as any) as any,
    currentFilters: serverParams,
    selectedIds,
  });


  return (
    <>
      <ListPageShell
        trashModule="specs"
        unitLabel="TSKT"
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        showFilter={showFilter}
        filterFields={FILTER_FIELDS.filter((f): f is FilterField => Boolean(f && (f as any).key))}
        filterValues={filterValues}
        headerFilters={
          <PageFilterTags
            items={filterTags}
            selectedKeys={
              statusFilter
                ? Array.isArray(statusFilter)
                  ? statusFilter
                  : [statusFilter as string]
                : []
            }
            onSelect={onStatusChange}
          />
        }
        actions={
          <div className="flex gap-2">
            <ExportImportButtons onExport={handleExport} exportLoading={isExporting} />
            <Button
              size="sm"
              className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
              onClick={() => navigate({ to: "/specs/create" as any })}
            >
              <Plus className="mr-2 h-3 w-3" /> Lập TSKT mới
            </Button>
          </div>
        }
      >
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
          <Table className={cn(selectionMode && "apv-select-mode")}>
            <TableHeader className="apv-table-header">
              <TableRow>
                <TableHead className={cn("apv-col-checkbox w-10 text-center")}>
                  <DataTableSelectAllCheckbox
                    rowSelection={
                      { allSelected, toggleSelectAll, selectionMode } as any
                    }
                    asChild
                  />
                </TableHead>
                <TableHead className="apv-col-code font-semibold">
                  Mã TSKT
                </TableHead>
                <TableHead className="font-semibold">
                  Sản phẩm & Khách hàng
                </TableHead>
                <TableHead className="font-semibold">Kích thước</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
              {showFilter && (
                <InlineFilterRow
                  columns={FILTER_FIELDS as any}
                  filterFields={FILTER_FIELDS.filter((f): f is FilterField => Boolean(f && (f as any).key))}
                  filterValues={filterValues}
                  onFilterChange={onFilterChange}
                />
              )}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className={cn("px-4 apv-col-checkbox")} />
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-8 w-8 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : displayItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-32 text-center text-slate-500 italic"
                  >
                    Không tìm thấy dữ liệu thông số kỹ thuật
                  </TableCell>
                </TableRow>
              ) : (
                displayItems.map((item: any) => {
                  const itemId = item.tsktId || item.specId || item.id;
                  return (
                    <DataTableRow
                      key={itemId}
                      id={itemId}
                      rowSelection={
                        {
                          selectedIds,
                          handleRowMouseDown,
                          handleRowMouseUp,
                          handleRowMouseLeave,
                          wasLongPress,
                          selectionMode,
                          handleRowSelect,
                        } as any
                      }
                      onClick={() => {
                        if (selectionMode) return;
                        navigate({
                          to: "/specs/$specId",
                          params: { specId: item.id },
                        });
                      }}
                    >
                      <TableCell
                        className={cn("px-4 apv-col-checkbox")}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DataTableRowCheckbox
                          id={itemId}
                          rowSelection={{ selectedIds, toggleSelect } as any}
                          asChild
                        />
                      </TableCell>
                      <TableCell className="apv-col-code py-1.5">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span className="font-bold text-rose-600 text-[13px] leading-none group-hover:underline">
                            {itemId}
                          </span>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] px-1.5 py-0 h-4 border-none leading-none rounded-sm font-medium",
                              STATUS_ITEMS.find((s) => s.key === item.status)
                                ?.color || "bg-slate-100 text-slate-600",
                            )}
                          >
                            {STATUS_ITEMS.find((s) => s.key === item.status)
                              ?.label || item.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="font-medium text-[12px] text-slate-900 leading-tight">
                            {item.productName}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-500 font-mono leading-none">
                              {item.productCode}
                            </span>
                            <span className="text-[11px] text-slate-700 font-medium leading-none flex items-center gap-1">
                              <User className="w-3 h-3 text-slate-400" />{" "}
                              {item.customerName}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="text-[12px] text-slate-700 leading-none">
                            {typeof item.finishedSize === "object" &&
                            item.finishedSize !== null
                              ? (item.finishedSize as any).length_mm ||
                                (item.finishedSize as any).finishedSizeLength
                                ? [
                                    (item.finishedSize as any).length_mm ||
                                      (item.finishedSize as any)
                                        .finishedSizeLength,
                                    (item.finishedSize as any).width_mm ||
                                      (item.finishedSize as any)
                                        .finishedSizeWidth,
                                    (item.finishedSize as any).height_mm ||
                                      (item.finishedSize as any)
                                        .finishedSizeHeight,
                                  ]
                                    .filter(Boolean)
                                    .join(" x ") + " mm"
                                : JSON.stringify(item.finishedSize)
                              : item.finishedSize || "—"}
                          </span>
                          <span className="text-[10px] text-slate-500 leading-none flex items-center gap-1">
                            <Clock className="w-3 h-3 text-slate-400" />{" "}
                            {formatDate(item.createdDate)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className="px-4 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-7 w-7 p-0">
                                <MoreHorizontal className="h-4 w-4 text-slate-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-40 z-[9999]"
                            >
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                                Tài liệu
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => setPrintSpec(item)}
                              >
                                <FileText className="mr-2 h-3.5 w-3.5" /> Bản vẽ
                                TSKT
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({
                                    to: "/specs/$specId/edit",
                                    params: { specId: item.id },
                                  })
                                }
                              >
                                <Pencil className="mr-2 h-3.5 w-3.5" /> Chỉnh
                                sửa
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </DataTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </ListPageShell>

      <PrintDialog
        open={!!printSpec}
        onClose={() => setPrintSpec(null)}
        type="spec"
        data={printSpec}
      />
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
    </>
  );
}
